import React, { useState } from 'react';
import { Alert, StyleSheet, View } from 'react-native';
import { Button, Text, TextInput, useTheme } from 'react-native-paper';
import {
  CalcCard,
  CalcSection,
  CalcShell,
  FormulaBlock,
  ResultBlock,
  formulaTextStyles,
  resultTextStyles,
} from '../../../components/shell';
import { DiagramCleanroomACH } from '../../../components/diagrams';
import {
  calcAchFromCfm,
  calcCfmFromAch,
  parseNumber,
  requireNonNegative,
  requirePositive,
  round,
} from '../../../utils/cleanroom';
import { useUnits } from '../../../context/UnitsContext';
import { C } from '../../../theme/tokens';

const CleanroomAirChangesPerHourScreen = () => {
  const theme = useTheme();
  const { unit, toImp, fromImp } = useUnits();
  const [roomVolume, setRoomVolume] = useState('');
  const [airChanges, setAirChanges] = useState('');
  const [cfmRequired, setCfmRequired] = useState('');
  const inputStyle = [styles.input, { backgroundColor: theme.colors.surfaceVariant, color: theme.colors.onSurface }];
  const inputColors = { textColor: theme.colors.onSurface, placeholderTextColor: theme.colors.onSurfaceVariant };

  const calculateMissingValue = () => {
    const values = [roomVolume, airChanges, cfmRequired];
    const filledCount = values.filter((v) => v.trim() !== '').length;

    if (filledCount < 2) {
      Alert.alert('Error', 'Please fill in at least 2 values to calculate the missing one');
      return;
    }

    if (filledCount === 3) {
      Alert.alert('Info', 'All values are filled. Clear one value to calculate it.');
      return;
    }

    // All math is performed in Imperial (CFM, ft³). Inputs are parsed from
    // the displayed unit and outputs are rendered in the displayed unit.
    if (cfmRequired.trim() === '') {
      const vImp = toImp(roomVolume, 'volume');
      if (!Number.isFinite(vImp)) return Alert.alert('Error', 'Room volume must be a number');
      const vOk = requirePositive(vImp, 'Room volume');
      if ('error' in vOk) return Alert.alert('Error', vOk.error);

      const a = parseNumber(airChanges, 'Air changes per hour (ACH)');
      if ('error' in a) return Alert.alert('Error', a.error);
      const aOk = requirePositive(a.value, 'Air changes per hour');
      if ('error' in aOk) return Alert.alert('Error', aOk.error);

      setCfmRequired(fromImp(calcCfmFromAch(vImp, a.value), 'flow', 2));
      return;
    }

    if (roomVolume.trim() === '') {
      const cImp = toImp(cfmRequired, 'flow');
      if (!Number.isFinite(cImp)) return Alert.alert('Error', 'Required airflow must be a number');
      const cOk = requireNonNegative(cImp, 'Required airflow');
      if ('error' in cOk) return Alert.alert('Error', cOk.error);

      const a = parseNumber(airChanges, 'Air changes per hour (ACH)');
      if ('error' in a) return Alert.alert('Error', a.error);
      const aOk = requirePositive(a.value, 'Air changes per hour');
      if ('error' in aOk) return Alert.alert('Error', aOk.error);

      const volumeImp = (cImp * 60) / a.value;
      setRoomVolume(fromImp(volumeImp, 'volume', 2));
      return;
    }

    if (airChanges.trim() === '') {
      const cImp = toImp(cfmRequired, 'flow');
      if (!Number.isFinite(cImp)) return Alert.alert('Error', 'Required airflow must be a number');
      const cOk = requireNonNegative(cImp, 'Required airflow');
      if ('error' in cOk) return Alert.alert('Error', cOk.error);

      const vImp = toImp(roomVolume, 'volume');
      if (!Number.isFinite(vImp)) return Alert.alert('Error', 'Room volume must be a number');
      const vOk = requirePositive(vImp, 'Room volume');
      if ('error' in vOk) return Alert.alert('Error', vOk.error);

      setAirChanges(String(round(calcAchFromCfm(vImp, cImp), 2)));
    }
  };

  const clearAll = () => {
    setRoomVolume('');
    setAirChanges('');
    setCfmRequired('');
  };

  return (
    <CalcShell
      title="Air Changes Per Hour"
      subtitle={`Calculate air changes, airflow, or room volume using CFM = (Room Volume × Air Changes/Hour) / 60`}
      diagram={<DiagramCleanroomACH />}
      outputSlots={{
        cfm: cfmRequired ? toImp(cfmRequired, 'flow') : undefined,
        volume_cuft: roomVolume ? toImp(roomVolume, 'volume') : undefined,
        air_changes_per_hour: airChanges ? parseFloat(airChanges) : undefined,
      }}
      onPrefill={(values) => {
        if (values.cfm != null && !cfmRequired) setCfmRequired(fromImp(values.cfm, 'flow', 2));
        if (values.volume_cuft != null && !roomVolume) setRoomVolume(fromImp(values.volume_cuft, 'volume', 2));
        if (values.air_changes_per_hour != null && !airChanges) setAirChanges(values.air_changes_per_hour.toFixed(2));
      }}
    >
      <CalcSection>
        <CalcCard>
          <FormulaBlock
            accentColor={C.accent}
            title="Formula"
            description={`Where: Airflow = ${unit('flow')}, Room Volume = ${unit('volume')}, Air Changes/Hour = ACH`}
          >
            <Text style={formulaTextStyles.formulaText}>Airflow = (Room Volume × Air Changes/Hour) / 60</Text>
          </FormulaBlock>

          <View style={styles.inputRow}>
            <TextInput
              label={`Room Volume (${unit('volume')})`}
              value={roomVolume}
              onChangeText={setRoomVolume}
              keyboardType="numeric"
              mode="outlined"
              style={inputStyle}
              {...inputColors}
            />
          </View>

          <View style={styles.inputRow}>
            <TextInput
              label="Air Changes/Hour (ACH)"
              value={airChanges}
              onChangeText={setAirChanges}
              keyboardType="numeric"
              mode="outlined"
              style={inputStyle}
              {...inputColors}
            />
          </View>

          <View style={styles.inputRow}>
            <TextInput
              label={`Required Airflow (${unit('flow')})`}
              value={cfmRequired}
              onChangeText={setCfmRequired}
              keyboardType="numeric"
              mode="outlined"
              style={inputStyle}
              {...inputColors}
            />
          </View>

          <View style={styles.buttonContainer}>
            <Button
              mode="contained"
              onPress={calculateMissingValue}
              style={styles.button}
              labelStyle={styles.buttonLabel}
            >
              Calculate
            </Button>
            <Button
              mode="outlined"
              onPress={clearAll}
              style={styles.clearButton}
              labelStyle={styles.clearButtonLabel}
            >
              Clear All
            </Button>
          </View>

          {(roomVolume || airChanges || cfmRequired) && (
            <ResultBlock accentColor={C.accent} label="Current Values:">
              {roomVolume && <Text style={resultTextStyles.value}>Room Volume: {roomVolume} {unit('volume')}</Text>}
              {airChanges && <Text style={resultTextStyles.value}>Air Changes/Hour: {airChanges} ACH</Text>}
              {cfmRequired && <Text style={resultTextStyles.value}>Required Airflow: {cfmRequired} {unit('flow')}</Text>}
            </ResultBlock>
          )}
        </CalcCard>
      </CalcSection>
    </CalcShell>
  );
};

const styles = StyleSheet.create({
  inputRow: {
    marginBottom: 16,
  },
  input: {
    flex: 1,
    marginHorizontal: 6,
    borderRadius: 12,
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  button: {
    flex: 1,
    marginRight: 8,
  },
  buttonLabel: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  clearButton: {
    flex: 1,
    marginLeft: 8,
  },
  clearButtonLabel: {
    fontSize: 16,
  },
});

export default CleanroomAirChangesPerHourScreen;
